import { LoadingPage, useToast } from "@qubit/autoparts";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { useNavigate } from "react-router-dom";

import { clearClientConfigError } from "~/app/app.slice";
import { useAppDispatch, useAppSelector } from "~/app/store";
import { ClientConfigFailedDialog } from "~/components/settings/ClientConfigFailedDialog";
import useFlag from "~/config/flags";
import { useValidateFulfillmentCenter } from "~/hooks/useValidateFulfillmentCenter";
import { sendUserEventAction } from "~/redux/actions/userEvents";
import {
  selectFlagsReadyClientId,
  selectClientConfigReady,
  selectClientConfigError,
  selectServerConfig
} from "~/redux/selectors/appSelectors";
import { selectIsUserLoggedIn } from "~/redux/selectors/authSelectors";
import { clientConfigApi } from "~/redux/warehouse/clientConfig";

import { handleAuthCallback } from "./login.slice";

type AuthStatus = "idle" | "pending" | "success" | "failure";

const connector = connect(null, {
  sendUserEventAction
});

type PropsFromRedux = ConnectedProps<typeof connector>;
type AuthCallBackProps = PropsFromRedux;

function AuthCallback(props: AuthCallBackProps) {
  const { sendUserEventAction } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { errorToast } = useToast();
  const { t } = useTranslation();

  const { validateFc } = useValidateFulfillmentCenter();
  const loginPath = useAppSelector((state) => state.app.siteLoginPath);
  const loginPathToUse: string = loginPath || "/login";

  const { manualOpsEnabled } = useAppSelector(selectServerConfig);

  const clientConfigReady = useAppSelector(selectClientConfigReady);
  const clientConfigError = useAppSelector(selectClientConfigError);
  const isUserLoggedIn = useAppSelector(selectIsUserLoggedIn);

  const flagsReady = useAppSelector(selectFlagsReadyClientId);
  const { improvedErrorHandlingConfig } = useFlag();
  const [triggerGetClientConfig] =
    clientConfigApi.useLazyGetClientConfigQuery();

  const [authStatus, setAuthStatus] = useState<AuthStatus>("idle");
  const redirectingRef = useRef(false);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (authStatus !== "idle") return;
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
    setAuthStatus("pending");
    const performAuthCallback = async () => {
      const resultAction = await dispatch(handleAuthCallback());
      setAuthStatus(
        handleAuthCallback.fulfilled.match(resultAction) ? "success" : "failure"
      );
    };
    void performAuthCallback();
  }, [authStatus, dispatch]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (authStatus === "failure") {
      navigate(loginPathToUse);
      return;
    }
    if (clientConfigError) {
      if (!improvedErrorHandlingConfig) {
        navigate(isUserLoggedIn ? "/logout" : loginPathToUse, {
          replace: true
        });
      }
      return;
    }
    if (!clientConfigReady) return;
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (authStatus !== "success") return;
    if (redirectingRef.current) return;
    redirectingRef.current = true;

    const handleSuccess = async () => {
      try {
        await sendUserEventAction("UserLoggedIn");
      } catch {
        errorToast("Failed to send user event action");
      }

      const abbreviation = await validateFc();

      const nextPage = manualOpsEnabled ? "/" : "/autostore-main";
      const fcParam = abbreviation
        ? `?fc=${encodeURIComponent(abbreviation)}`
        : "";
      window.location.href = `${nextPage}${fcParam}`;
    };

    void handleSuccess();
  }, [
    clientConfigReady,
    clientConfigError,
    authStatus,
    errorToast,
    validateFc,
    manualOpsEnabled,
    navigate,
    sendUserEventAction,
    loginPathToUse,
    improvedErrorHandlingConfig,
    isUserLoggedIn
  ]);

  useEffect(() => {
    if (
      flagsReady &&
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      authStatus === "success" &&
      !clientConfigReady &&
      !clientConfigError
    ) {
      void triggerGetClientConfig();
    }
  }, [
    flagsReady,
    authStatus,
    clientConfigReady,
    clientConfigError,
    triggerGetClientConfig
  ]);

  if (
    clientConfigError &&
    improvedErrorHandlingConfig &&
    authStatus === "success"
  ) {
    return (
      <>
        <LoadingPage loadingMessage={t("logging on")} />
        <ClientConfigFailedDialog
          open
          onRetry={() => {
            dispatch(clearClientConfigError());
          }}
          onGoToLogout={() => {
            navigate(isUserLoggedIn ? "/logout" : loginPathToUse, {
              replace: true
            });
          }}
        />
      </>
    );
  }

  return <LoadingPage loadingMessage={t("logging on")} />;
}

export default connector(AuthCallback);
